import React, { useEffect, useState } from "react";
import useAxios from "axios-hooks";
import {
  RiSearchLine,
  RiFilterLine,
} from "react-icons/ri";
import { useSetRecoilState } from "recoil";
import {
  Box,
  Flex,
  FormLabel,
  Input,
  Select,
  Button,
  FormControl,
  Text,
  Icon,
  HStack,
  VStack,
  InputGroup,
  useColorModeValue,
  Grid,
  Heading,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { motion } from "framer-motion";
import {
  FaCalendarAlt,
  FaListAlt,
} from "react-icons/fa";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import ChargingSessionsTable from "./components/ChargingSessionsTable";
import { chargingSessionTableHeaders } from "./variables/tableHeaders";

const MotionBox = motion(Box);
const MotionFlex = motion(Flex);

const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const ChargingSessionsScreen = () => {
  const [sessions, setSessions] = useState([]);
  const [sources, setSources] = useState([]);
  const [parties, setParties] = useState([]);
  const setToast = useSetRecoilState(toastState);
  const now = new Date();
  const startOfYear = new Date(now.getFullYear(), 0, 1);

  const [{ data, loading, error }, execute] = useAxios(
    { method: "GET", url: "/api/chargingSession/all" },
    { manual: true }
  );

  const [{ sourceData, sourceLoading, sourceError }, getAllSources] = useAxios(
    {
      method: "GET",
      url: `/api/source/all`,
    },
    { manual: true }
  );

  const {
    control,
    watch,
    formState: { errors, isValid },
    getValues,
  } = useForm({
    mode: "onChange",
  });

  const sourceValue = watch("Source");
  const fromValue = watch("From");
  const toValue = watch("To");
  const statusValue = watch("Status");
  const partyIdValue = watch("PartyId");

  const options = sources.map((ele, index) => {
    return <option key={index}>{ele.Name}</option>;
  }) || <div></div>;

  const getSources = async () => {
    try {
      const response = await getAllSources();
      setSources(response.data.data);
      setToast({
        status: "success",
        message: "Sources fetched successfully",
        title: "Success",
        position: "top-right",
        duration: 5000,
      });
    } catch (error) {
      logger.log("error occured");
      setToast({
        title: "Error fetching sources",
        status: "error",
        duration: 5000,
        iseOpen: true,
      });
    }
  };

  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const hours = String(today.getHours()).padStart(2, "0");
    const minutes = String(today.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  function formatDate(date, hours = 0, minutes = 0) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;
    const formattedTime = `${String(hours).padStart(2, "0")}:${String(
      minutes
    ).padStart(2, "0")}`;
    return `${formattedDate}T${formattedTime}`;
  }

  const fetchSessions = async () => {
    const currentDate = new Date();
    const startOfYear = new Date(currentDate.getFullYear(), 0, 1);

    const formValues = getValues();
    let params = {};
    params["from"] = formValues.From || formatDate(startOfYear);
    params["to"] = formValues.To || getCurrentDate();
    params["source"] = formValues.Source;
    params["status"] = formValues.Status;
      if (sourceValue === "OCPI" && formValues.PartyId) {
      params["partyId"] = formValues.PartyId;
    }

    try {
      const response = await execute({ params });
      const result = response.data.data;
      const sessionsData = result?.charging_sessions || [];
      setSessions(sessionsData);
      setParties(result?.ocpi_parties || []);
    } catch (error) {
      logger.log("error occurred");
    }
  };

  useEffect(() => {
    getSources();
  }, []);

  useEffect(() => {
    fetchSessions();
  }, [sourceValue, fromValue, toValue, statusValue, partyIdValue]);

  const themeColor = "#23A979";
  const inputBgColor = useColorModeValue("white", "gray.700");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <GlassMorphismCard mb={{ base: 4, md: 6 }}>
        <Box
          p={{ base: 4, md: 6 }}
          borderBottom="1px solid"
          borderColor={borderColor}
        >
          <Flex
            justify="space-between"
            align="center"
            mb={{ base: 3, md: 4 }}
            direction={{ base: "column", sm: "row" }}
            gap={{ base: 2, sm: 0 }}
          >
            <Heading size={{ base: "sm", md: "md" }} color="gray.700">
              <Icon as={RiFilterLine} mr={2} color={themeColor} />
              Session Filters
            </Heading>
            <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
              Filter by date range and source
            </Text>
          </Flex>
        </Box>

        <Box p={{ base: 4, md: 6 }}>
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            }}
            gap={{ base: 3, md: 6 }}
            mb={{ base: 4, md: 6 }}
          >
            {[
              {
                label: "From Date",
                type: "datetime-local",
                name: "From",
                icon: FaCalendarAlt,
                defaultValue: formatDate(startOfYear),
              },
              {
                label: "To Date",
                type: "datetime-local",
                name: "To",
                icon: FaCalendarAlt,
                defaultValue: getCurrentDate(),
              },
              {
                label: "Status",
                type: "select",
                name: "Status",
                icon: RiFilterLine,
                defaultValue: "",
              },
              {
                label: "Origin",
                type: "select",
                name: "Source",
                icon: RiFilterLine,
                defaultValue: "",
              },
              ...(sourceValue === "OCPI"
                ? [
                    {
                      label: "Party",
                      type: "select",
                      name: "PartyId",
                      icon: RiFilterLine,
                      defaultValue: "",
                    },
                  ]
                : []),
            ].map((item) => {
              return (
                <Controller
                  key={item.name}
                  name={item.name}
                  control={control}
                  defaultValue={item.defaultValue}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel
                        fontSize={{ base: "xs", md: "sm" }}
                        fontWeight="medium"
                        color="gray.600"
                      >
                        <Flex align="center">
                          <Icon
                            as={item.icon}
                            mr={2}
                            color={themeColor}
                            boxSize={{ base: 3, md: 4 }}
                          />
                          {item.label}
                        </Flex>
                      </FormLabel>
                      {item.type === "datetime-local" && (
                        <InputGroup>
                          <Input
                            {...field}
                            bg={inputBgColor}
                            borderRadius="md"
                            borderColor={borderColor}
                            focusBorderColor={themeColor}
                            fontSize={{ base: "xs", md: "sm" }}
                            size={{ base: "sm", md: "md" }}
                            type={item.type}
                            boxShadow="sm"
                            _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                            _hover={{ borderColor: themeColor }}
                            cursor="pointer"
                            onClick={(e) => {
                              e.currentTarget.showPicker();
                            }}
                            sx={{
                              "&::-webkit-calendar-picker-indicator": {
                                cursor: "pointer",
                                opacity: 0.8,
                                transition: "all 0.2s",
                                filter:
                                  "invert(70%) sepia(59%) saturate(2532%) hue-rotate(334deg) brightness(101%) contrast(96%)",
                                "&:hover": {
                                  opacity: 1,
                                  transform: "scale(1.1)",
                                },
                              },
                            }}
                          />
                        </InputGroup>
                      )}
                      {item.type === "select" && item.name === "Status" && (
                        <Select
                          {...field}
                          bg={inputBgColor}
                          borderRadius="md"
                          borderColor={borderColor}
                          focusBorderColor={themeColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          size={{ base: "sm", md: "md" }}
                          boxShadow="sm"
                          _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                          _hover={{ borderColor: themeColor }}
                        >
                          <option value="">All Statuses</option>
                          <option value="SCHEDULED">Scheduled</option>
                          <option value="STARTED">Started</option>
                          <option value="ONGOING">Ongoing</option>
                          <option value="STOPPED">Stopped</option>
                          <option value="COMPLETED">Completed</option>
                        </Select>
                      )}
                      {item.type === "select" && item.name === "Source" && (
                        <Select
                          {...field}
                          bg={inputBgColor}
                          borderRadius="md"
                          borderColor={borderColor}
                          focusBorderColor={themeColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          size={{ base: "sm", md: "md" }}
                          boxShadow="sm"
                          _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                          _hover={{ borderColor: themeColor }}
                        >
                          <option value="">All Origins</option>
                          <option value="PARTNER">PARTNER</option>
                          <option value="CMS-UI">CMS-UI</option>
                          <option value="OCPI">OCPI</option>
                          <option value="rfid_authorized">
                            RFID-AUTHORIZED
                          </option>
                          {options}
                        </Select>
                      )}
                      {item.type === "select" && item.name === "PartyId" && (
                        <Select
                          {...field}
                          bg={inputBgColor}
                          borderRadius="md"
                          borderColor={borderColor}
                          focusBorderColor={themeColor}
                          fontSize={{ base: "xs", md: "sm" }}
                          size={{ base: "sm", md: "md" }}
                          boxShadow="sm"
                          _focus={{ boxShadow: `0 0 0 1px ${themeColor}` }}
                          _hover={{ borderColor: themeColor }}
                        >
                          <option value="">All</option>
                          {parties.map((p, i) => (
                            <option key={i} value={p}>
                              {p}
                            </option>
                          ))}
                        </Select>
                      )}
                    </FormControl>
                  )}
                />
              );
            })}
          </Grid>

          <Flex
            justify={{ base: "center", md: "flex-end" }}
            gap={{ base: 2, md: 3 }}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
          >
            <Button
              leftIcon={<RiSearchLine />}
              bg={themeColor}
              color="white"
              _hover={{ bg: `${themeColor}90`, transform: "translateY(-2px)" }}
              _active={{ bg: `${themeColor}d0` }}
              borderRadius="md"
              onClick={fetchSessions}
              isLoading={loading}
              transition="all 0.2s"
              boxShadow="md"
              w={{ base: "100%", sm: "auto" }}
              fontSize={{ base: "sm", md: "md" }}
              size={{ base: "sm", md: "md" }}
            >
              Search Sessions
            </Button>
          </Flex>
        </Box>
      </GlassMorphismCard>

      {sessions.length > 0 ? (
        <ChargingSessionsTable
          columnsData={chargingSessionTableHeaders}
          tableData={sessions}
        />
      ) : (
        <GlassMorphismCard p={0}>
          <MotionFlex
            direction="column"
            align="center"
            justify="center"
            py={{ base: 12, md: 16 }}
            px={{ base: 4, md: 0 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Icon
              as={FaListAlt}
              boxSize={{ base: 10, md: 12 }}
              color={themeColor}
              mb={4}
              opacity={0.6}
            />
            <Text
              fontSize={{ base: "lg", md: "xl" }}
              fontWeight="bold"
              mb={2}
            >
              No Sessions Found
            </Text>
            <Text
              color="gray.500"
              textAlign="center"
              maxW="md"
              fontSize={{ base: "sm", md: "md" }}
              px={{ base: 4, md: 0 }}
            >
              There are no charging sessions matching your filter criteria. Try
              changing your filters or select a different date range.
            </Text>
          </MotionFlex>
        </GlassMorphismCard>
      )}
    </MotionBox>
  );
};

export default ChargingSessionsScreen;
